/**
 * Web Health proposal review: the twin of `HealthProposalReview.tsx`.
 *
 * The finding title, the current text under a collapsed "Original"
 * disclosure, the suggested text in an editable textarea (`dir="auto"`), a
 * short hint, the primary Save (off while saving, when empty, or when
 * unchanged from the original) and a quiet "Keep original". Errors show
 * inline in the validation red, never as a toast.
 */
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { getFindingTitleKey } from './cv-health-copy';
import type { HealthProposalReviewProps } from './health-props';

export type { HealthProposalReviewProps };

const DEFAULT_SAVE_LABEL_KEY = 'cvHealth.actions.saveChange';

export function HealthProposalReview({
  finding,
  onSave,
  onKeepOriginal,
  saving,
  error,
  saveLabelKey = DEFAULT_SAVE_LABEL_KEY,
  onTextChange,
}: HealthProposalReviewProps) {
  const { t } = useTranslation('common');
  const ids = useId().replace(/:/g, '');
  const originalId = `tabbio-health-original-${ids}`;
  const fieldId = `tabbio-health-suggested-${ids}`;
  const hintId = `tabbio-health-hint-${ids}`;
  const errorId = `tabbio-health-error-${ids}`;
  const before = finding.proposal?.before ?? '';
  const after = finding.proposal?.after ?? '';
  const [text, setText] = useState(after);
  const [originalOpen, setOriginalOpen] = useState(false);

  // A different finding (or a refreshed proposal) starts from its suggestion.
  useEffect(() => {
    setText(after);
    setOriginalOpen(false);
  }, [finding.key, after]);

  const trimmed = text.trim();
  const canSave = !saving && trimmed.length > 0 && trimmed !== before.trim();

  return (
    <div className="tabbio-health tabbio-health-review">
      <h2 className="tabbio-health-review-heading">{t(getFindingTitleKey(finding))}</h2>

      <div>
        <button
          type="button"
          className="tabbio-health-about-header"
          aria-expanded={originalOpen}
          aria-controls={originalId}
          onClick={() => setOriginalOpen((open) => !open)}
        >
          <span>{t('cvHealth.actions.original')}</span>
          <span className={`tabbio-health-chevron${originalOpen ? ' is-open' : ''}`}>
            <MaterialSymbol name="expand_more" size={18} />
          </span>
        </button>
        <div
          id={originalId}
          className={`tabbio-health-disclosure${originalOpen ? ' is-open' : ''}`}
          aria-hidden={!originalOpen}
          inert={!originalOpen}
        >
          <div>
            <div className="tabbio-health-quote is-spaced">
              {before ? (
                <p className="tabbio-health-excerpt ph-no-capture" dir="auto">
                  {before}
                </p>
              ) : (
                <p className="tabbio-health-excerpt is-muted">{t('cvHealth.actions.nothingYet')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="tabbio-health-field">
        <label className="tabbio-health-label is-field" htmlFor={fieldId}>
          {t('cvHealth.actions.suggested')}
        </label>
        <textarea
          id={fieldId}
          className="tabbio-health-textarea ph-no-capture"
          dir="auto"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            onTextChange?.(event.target.value);
          }}
          readOnly={saving}
          rows={5}
          aria-describedby={error ? `${hintId} ${errorId}` : hintId}
          aria-invalid={error ? true : undefined}
        />
        <p id={hintId} className="tabbio-health-hint">
          {t('cvHealth.actions.editHint')}
        </p>
      </div>

      {error ? (
        <p id={errorId} className="tabbio-health-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="tabbio-health-review-actions">
        <button
          type="button"
          className="tabbio-health-btn is-primary is-block"
          onClick={() => {
            void onSave(trimmed);
          }}
          disabled={!canSave}
          aria-busy={saving || undefined}
        >
          {t(saveLabelKey)}
        </button>
        <button
          type="button"
          className="tabbio-health-btn is-text is-block"
          onClick={onKeepOriginal}
          disabled={saving}
        >
          {t('cvHealth.actions.keepOriginal')}
        </button>
      </div>
    </div>
  );
}

export default HealthProposalReview;
